import type { RefObject } from "react";
import { BasicExtensionFactory, Canvas } from "../types/canvasTypes";
import { CANVAS_INITIALIZED_EVENT, CANVAS_PADDING } from "../constants/canvasConstants";
import { noop } from "@/lib/utils";
import { findViewportShape } from "../utils/viewportUtils";
import { triggerObjectModifiedEvent, triggerViewportModified } from "../helpers/canvasHelpers";

const NAME = "viewport-initializer";

export type UpdateCanvasAndViewportDimensionsFunction = () => void;
type ViewportSettingsReference = RefObject<{ width: number; height: number }>;

export const createViewportInitializerExtension: BasicExtensionFactory<{
  canvasWrapperElementRef: RefObject<HTMLDivElement>;
  viewportSettingsRef: ViewportSettingsReference;
  recalculateCanvasAndViewportDimensionsFunctionFactory: (fn: UpdateCanvasAndViewportDimensionsFunction) => void;
}> = ({ canvasWrapperElementRef, viewportSettingsRef, recalculateCanvasAndViewportDimensionsFunctionFactory }) => {
  let onCanvasInitialized: (() => void) | undefined;
  const [subscribeToResizeObserver, unsubscribeFromResizeObserver] =
    createResizeObserverSubscription(viewportSettingsRef);

  return {
    name: NAME,
    onInit: (canvas) => {
      onCanvasInitialized = () => {
        subscribeToResizeObserver?.(
          canvas,
          canvasWrapperElementRef,
          recalculateCanvasAndViewportDimensionsFunctionFactory
        );
      };
      canvas.on(CANVAS_INITIALIZED_EVENT, onCanvasInitialized);
    },
    onDestroy: (canvas) => {
      canvas.off(CANVAS_INITIALIZED_EVENT, onCanvasInitialized);
      unsubscribeFromResizeObserver?.();
    },
  };
};

function createResizeObserverSubscription(viewportSettingsRef: ViewportSettingsReference) {
  if (typeof ResizeObserver === "undefined") {
    console.warn(`[createResizeObserverSubscription] can't subscribe to ResizeObserver: ResizeObserver is undefined`);
    return [noop, noop];
  }

  let resizeObserver: ResizeObserver | undefined;
  let canvasWrapperElement: HTMLDivElement | null = null;

  return [
    (
      canvas: Canvas,
      canvasWrapperElementRef: RefObject<HTMLDivElement>,
      recalculateCanvasAndViewportDimensionsFunctionFactory: (fn: UpdateCanvasAndViewportDimensionsFunction) => void
    ) => {
      canvasWrapperElement = canvasWrapperElementRef.current;

      if (!canvasWrapperElement) return;

      const recalculateAndUpdateCanvasAndViewportDimensions = () => {
        const viewport = findViewportShape(canvas);
        if (!viewport) return;

        const { current: viewportSettings } = viewportSettingsRef;

        if (!viewportSettings || !canvasWrapperElement) return;

        const { clientWidth, clientHeight } = canvasWrapperElement;

        // If dimensions for viewport have been changed set and trigger modification event
        if (viewport.width !== viewportSettings.width || viewport.height !== viewportSettings.height) {
          viewport.set({
            width: viewportSettings.width,
            height: viewportSettings.height,
          });
          triggerObjectModifiedEvent(canvas);
          triggerViewportModified(canvas);
        }

        updateCanvasDimensions({
          canvas,
          clientWidth,
          clientHeight,
          viewportWidth: viewportSettings.width,
          viewportHeight: viewportSettings.height,
        });
      };

      resizeObserver = new ResizeObserver(recalculateAndUpdateCanvasAndViewportDimensions);
      resizeObserver.observe(canvasWrapperElement);

      // Init function which we can trigger manually in the future outside of this extension
      recalculateCanvasAndViewportDimensionsFunctionFactory(recalculateAndUpdateCanvasAndViewportDimensions);
    },
    () => {
      if (!canvasWrapperElement) {
        console.warn(
          `[createResizeObserverSubscription] can't unsubscribe from the canvasWrapperElement: canvasWrapperElement is undefined`
        );

        return;
      }

      resizeObserver?.unobserve(canvasWrapperElement);
    },
  ] as const;
}

// Helpers
function updateCanvasDimensions({
  canvas,
  clientWidth,
  clientHeight,
  viewportWidth,
  viewportHeight,
}: {
  canvas: Canvas;
  clientWidth: number;
  clientHeight: number;
  viewportWidth: number;
  viewportHeight: number;
}) {
  canvas.setWidth(clientWidth);
  canvas.setHeight(clientHeight);

  const scaleApproximation = getScale({ clientWidth, clientHeight, viewportWidth, viewportHeight });
  const padding = CANVAS_PADDING / scaleApproximation;

  const viewportOuterWidth = viewportWidth + padding * 2;
  const viewportOuterHeight = viewportHeight + padding * 2;

  const scale = getScale({
    clientWidth,
    clientHeight,
    viewportWidth: viewportOuterWidth,
    viewportHeight: viewportOuterHeight,
  });

  const containerWidth = clientWidth / scale;
  const containerHeight = clientHeight / scale;

  const viewportLeftCord = (containerWidth - viewportWidth) / 2;
  const viewportTopCord = (containerHeight - viewportHeight) / 2;

  canvas.viewportTransform = [1, 0, 0, 1, viewportLeftCord, viewportTopCord];
  canvas.setZoom(scale);
  canvas.renderAll();
}

function getScale({
  clientWidth,
  clientHeight,
  viewportWidth,
  viewportHeight,
}: {
  clientWidth: number;
  clientHeight: number;
  viewportWidth: number;
  viewportHeight: number;
}) {
  const scaleWidth = clientWidth / viewportWidth;
  const scaleHeight = clientHeight / viewportHeight;

  return Math.min(scaleWidth, scaleHeight);
}
